/**
 * EntityChangeCardShell - Shared wrapper for entity change cards.
 *
 * Provides collapsible card with colored left border, header with icon/name/badges.
 * Shows an action pill (Transferred / Skipped) pinned to the top-right of pending cards.
 */

import { ArrowRightLeft, ChevronDown, ChevronRight, SkipForward } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { cn } from '@/lib/utils';
import type {
  EntityChangeStatus,
  EntityChangeType,
} from '@/services/api/generated/entityChangesService';

import {
  CHANGE_TYPE_BORDER,
  CHANGE_TYPE_CONFIG,
} from './changeTypeConfig';

interface EntityChangeCardShellProps {
  changeType: EntityChangeType;
  name: string;
  description?: string | null;
  icon: React.ElementType;
  iconBgClassName: string;
  iconTextClassName: string;
  badges?: React.ReactNode;
  statusBadge?: React.ReactNode;
  changeCount: number;
  hasExpandedContent: boolean;
  defaultExpanded?: boolean;
  /** Current entity change status (pending / transferred / skipped). */
  entityStatus?: EntityChangeStatus;
  /** Called when user clicks Transferred or Skipped. */
  onStatusChange?: (newStatus: EntityChangeStatus) => void;
  /** Called when the entity name is clicked (e.g. to open a detail view). */
  onNameClick?: () => void;
  children: React.ReactNode;
}

export const EntityChangeCardShell: React.FC<EntityChangeCardShellProps> = ({
  changeType,
  name,
  description,
  icon: Icon,
  iconBgClassName,
  iconTextClassName,
  badges,
  statusBadge,
  changeCount,
  hasExpandedContent,
  defaultExpanded,
  entityStatus,
  onStatusChange,
  onNameClick,
  children,
}) => {
  const alreadyActioned = entityStatus === 'transferred' || entityStatus === 'skipped';
  const computedDefault =
    defaultExpanded ??
    (!alreadyActioned &&
      (changeType === 'created' || changeType === 'deleted' || changeCount > 0));

  const [expanded, setExpanded] = React.useState(computedDefault);

  const changeConfig = CHANGE_TYPE_CONFIG[changeType];
  const borderClass = CHANGE_TYPE_BORDER[changeType];

  const showActions = Boolean(onStatusChange && entityStatus === 'pending');

  return (
    <div
      className={cn(
        'relative border border-gray-200 rounded-md',
        borderClass && `border-l-4 ${borderClass}`,
      )}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => hasExpandedContent && setExpanded(!expanded)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
          hasExpandedContent
            ? 'cursor-pointer hover:bg-gray-50/50'
            : 'cursor-default',
          entityStatus === 'transferred' && 'opacity-50',
          entityStatus === 'skipped' && 'opacity-40',
        )}
      >
        {!hasExpandedContent && <div className="h-4 w-4 shrink-0" />}
        {hasExpandedContent && expanded && (
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
        )}
        {hasExpandedContent && !expanded && (
          <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
        )}

        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            iconBgClassName,
          )}
        >
          <Icon className={cn('h-4 w-4', iconTextClassName)} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {onNameClick ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNameClick();
                  }}
                  className="font-medium text-sm text-indigo-600 hover:text-indigo-800 whitespace-normal break-words text-left transition-colors"
                >
                  {name}
                </button>
              ) : (
              <span className="font-medium text-sm text-gray-900 whitespace-normal break-words">
                {name}
              </span>
            )}
            {badges}
          </div>
          {description && (
            <p className="text-xs text-gray-500 whitespace-normal break-words mt-0.5">
              {description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {entityStatus && entityStatus !== 'pending' && (
            <Badge
              color={entityStatus === 'transferred' ? 'green' : 'amber'}
              className="text-xs"
            >
              {entityStatus === 'transferred' ? 'Transferred' : 'Skipped'}
            </Badge>
          )}
          {statusBadge}
          <Badge color={changeConfig.color} className="text-xs">
            {changeConfig.label}
          </Badge>
          {changeCount > 0 && (
            <span className="text-xs text-gray-400">
              {changeCount} {changeCount === 1 ? 'change' : 'changes'}
            </span>
          )}
          {/* Inline action buttons */}
          {showActions && onStatusChange && (
            <div className="flex items-center gap-1 ml-1 rounded-md border border-gray-200 bg-white overflow-hidden">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange('transferred');
                }}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <ArrowRightLeft className="h-3 w-3 text-gray-400" />
                Transferred
              </button>
              <div className="w-px h-4 bg-gray-200" />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange('skipped');
                }}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <SkipForward className="h-3 w-3 text-gray-400" />
                Skip
              </button>
            </div>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && hasExpandedContent && (
        <div className="border-t border-gray-100 px-4 py-2.5 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};
